<template>
  <div 
    class="ui-overlay-container"
    @click.stop
  >
<div 
  :class="['custom-tooltip', { 'visible': isTooltipVisible, 'no-transition': isFirstRun }]" 
  :style="tooltipPosition"
>
      {{ tooltipText }}
    </div>

    <div class="ui-wrapper" :class="{ collapsed: !isUIVisible }">
      <header 
        class="scene-header"
        @mouseenter="isInfoVisible = true"
        @mouseleave="isInfoVisible = false"
      >
        <h1 class="main-title">稻城色韵</h1>
        <h2 class="subtitle">稻城人文自然色彩可视化图谱</h2>
      </header>
      <div class="project-info-panel" :class="{ visible: isInfoVisible }">
        <p>本项目构建了稻城地区独特的色彩谱系。通过交互式筛选，您可以观察不同季节、海拔和分类下稻城的色彩分布与变化；通过参数调节，您可以修改色彩图谱的呈现方式。</p>
      </div>

      <div class="panel-section category-filter">
        <div class="category-group" v-if="groupedCategories.nature.length > 0">
          <h4 class="category-subtitle">自然色彩</h4>
          <div class="tag-group">
            <span 
              v-for="cat in groupedCategories.nature" 
              :key="cat.original" 
              class="tag"
              :class="{ active: store.activeCategories.has(cat.original) }" 
              @click="toggleCategory(cat.original)"
              @mouseenter="showTooltip(cat.original, $event)"
              @mouseleave="hideTooltip"
            >
              {{ cat.display }}
            </span>
          </div>
        </div>
        <div class="category-group" v-if="groupedCategories.culture.length > 0">
          <h4 class="category-subtitle">人文色彩</h4>
          <div class="tag-group">
            <span 
              v-for="cat in groupedCategories.culture" 
              :key="cat.original" 
              class="tag"
              :class="{ active: store.activeCategories.has(cat.original) }" 
              @click="toggleCategory(cat.original)"
              @mouseenter="showTooltip(cat.original, $event)"
              @mouseleave="hideTooltip"
            >
              {{ cat.display }}
            </span>
          </div>
        </div>
      </div>
      
      <div class="panel-section season-filter" :class="{ disabled: !isSeasonFilterActive }">
        <span class="filter-label">季节</span>
        <div class="button-group">
          <button v-for="season in seasons" :key="season" :class="{ active: activeSeasons.has(season) }"
            @click="toggleSeason(season)" :disabled="!isSeasonFilterActive">
            <span>{{ season }}</span>
          </button>
        </div>
      </div>

      <div class="panel-section altitude-filter">
        <span class="filter-label">海拔</span>
        <div class="range-slider">
          <div class="slider-track-container">
            <div class="slider-track"></div>
            <div class="slider-track-highlight" :style="rangeHighlightStyle"></div>
            <input type="range" :min="ALTITUDE_CONFIG.min" :max="ALTITUDE_CONFIG.max" :step="ALTITUDE_CONFIG.step"
              :value="altitudeMin" @input="onMinAltitudeChange" class="thumb thumb-z-3" />
            <input type="range" :min="ALTITUDE_CONFIG.min" :max="ALTITUDE_CONFIG.max" :step="ALTITUDE_CONFIG.step"
              :value="altitudeMax" @input="onMaxAltitudeChange" class="thumb thumb-z-4" />
          </div>
           <div class="slider-values">
            <span>{{ altitudeMin }}(m)</span>
            <span>{{ altitudeMax }}(m)</span>
          </div>
        </div>
      </div>

      <footer class="scene-footer">
        <div class="status-text">
          当前渲染 {{ store.colorPoints.length }} 颜色
        </div>
        <div class="hint-text">
          右键隐藏筛选 / 左键显示筛选
        </div>
      </footer>
    </div>
    
    <div class="control-toggle-wrapper">
    <button 
      class="toggle-ui-button" 
      @click="toggleUIVisibility" 
      @click.prevent="openDebugPanelCentered"
      title="左键：切换筛选器 | 右键：打开调试面板"
    >
      <svg class="icon-sliders" :class="{ collapsed: !isUIVisible }" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="4" y1="21" x2="4" y2="14"></line><line x1="4" y1="10" x2="4" y2="3"></line><line x1="12" y1="21" x2="12" y2="12"></line><line x1="12" y1="8" x2="12" y2="3"></line><line x1="20" y1="21" x2="20" y2="16"></line><line x1="20" y1="12" x2="20" y2="3"></line><line x1="1" y1="14" x2="7" y2="14"></line><line x1="9" y1="8" x2="15" y2="8"></line><line x1="17" y1="16" x2="23" y2="16"></line></svg>
    </button>
    <span class="toggle-label">显示/关闭-可视化参数调节</span>
  </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted  } from 'vue';
import { useMainStore } from '@/stores/mainStore';

const store = useMainStore();

// --- UI可见性状态 ---
const isUIVisible = ref(true);
const isInfoVisible = ref(false);

const toggleUIVisibility = () => {
  isUIVisible.value = !isUIVisible.value;
};

const openDebugPanelCentered = () => {
  store.showDebugPanel({ centered: true });
};

// --- Interaction Logic: Right-click hides, Left-click shows ---
const handleRightClick = (e) => {
  // Prevent hiding UI if right-clicking the toggle button
  if (!e.target.closest('.toggle-ui-button')) {
    e.preventDefault();
    isUIVisible.value = false;
  }
};

const handleLeftClick = (e) => {
  if (!isUIVisible.value && !e.target.closest('.toggle-ui-button')) {
    isUIVisible.value = true;
  }
};

// --- 生命周期钩子 ---
onMounted(() => {
  window.addEventListener('contextmenu', handleRightClick);
  window.addEventListener('click', handleLeftClick);
});

onUnmounted(() => {
  window.removeEventListener('contextmenu', handleRightClick);
  window.removeEventListener('click', handleLeftClick);
});

// --- 自定义悬浮提示逻辑 ---
const isTooltipVisible = ref(false);
const tooltipText = ref('');
const tooltipPosition = ref({ top: '0px', left: '0px' });
const isFirstRun = ref(true);

const showTooltip = (text, event) => {
  tooltipText.value = text;
  const rect = event.target.getBoundingClientRect();
  tooltipPosition.value = {
    top: `${rect.bottom + 8}px`,
    left: `${rect.left + rect.width / 2}px`,
  };

  isTooltipVisible.value = true;

  // 如果是第一次运行，我们在一小段时间后将其设置为false
  // 这样后续的悬浮就会有动画了
  if (isFirstRun.value) {
    setTimeout(() => {
      isFirstRun.value = false;
    }, 50); // 50毫秒的延迟足够了
  }
};

const hideTooltip = () => {
  isTooltipVisible.value = false;
};

// --- 季节 ---
const seasons = ['春', '夏', '秋', '冬'];
const activeSeasons = ref(new Set(['春', '夏', '秋', '冬'])); 

const toggleSeason = (season) => {
  if (activeSeasons.value.has(season)) {
    activeSeasons.value.delete(season);
  } else {
    activeSeasons.value.add(season);
  }
  store.setActiveSeasons(new Set(activeSeasons.value));
};

// --- 海拔 ---
const ALTITUDE_CONFIG = { min: 0, max: 6100, step: 100 };
const altitudeMin = ref(store.altitudeRange[0]);
const altitudeMax = ref(store.altitudeRange[1]);

const onMinAltitudeChange = (event) => {
  const value = parseInt(event.target.value);
  altitudeMin.value = Math.min(value, altitudeMax.value - ALTITUDE_CONFIG.step);
  store.setAltitudeRange([altitudeMin.value, altitudeMax.value]);
};
const onMaxAltitudeChange = (event) => {
  const value = parseInt(event.target.value);
  altitudeMax.value = Math.max(value, altitudeMin.value + ALTITUDE_CONFIG.step);
  store.setAltitudeRange([altitudeMin.value, altitudeMax.value]);
};

const rangeHighlightStyle = computed(() => {
  const minPercent = ((altitudeMin.value - ALTITUDE_CONFIG.min) / (ALTITUDE_CONFIG.max - ALTITUDE_CONFIG.min)) * 100;
  const maxPercent = ((altitudeMax.value - ALTITUDE_CONFIG.min) / (ALTITUDE_CONFIG.max - ALTITUDE_CONFIG.min)) * 100;
  return {
    left: `${minPercent}%`,
    right: `${100 - maxPercent}%`,
  };
});

// --- 分类 ---
const categoryNameMap = {
  '雪山': '圣白雪巅',
  '湖泊': '澄莹冰湖',
  '草甸': '斑斓甸彩',
  '地质': '墨灰石岩',
  '建筑': '赭白藏垣',
  '服饰': '玄红彩织',
  '日用': '烁银乌陶',
  '壁画': '藏彩灵绘'
};

const natureOrder = ['圣白雪巅', '澄莹冰湖', '斑斓甸彩', '墨灰石岩'];
const cultureOrder = ['赭白藏垣', '玄红彩织', '烁银乌陶', '藏彩灵绘'];
const CULTURAL_CATEGORIES_DB = ['建筑', '服饰', '日用', '壁画'];

const groupedCategories = computed(() => {
  const allAvailableCategories = new Map();
  store.allElements.forEach(el => {
    const originalName = el.full_category?.name;
    if (originalName && categoryNameMap[originalName]) {
      allAvailableCategories.set(originalName, categoryNameMap[originalName]);
    }
  });

  const createOrderedList = (orderArray) => {
    return orderArray
      .map(displayName => {
        const originalName = Object.keys(categoryNameMap).find(key => categoryNameMap[key] === displayName);
        if (allAvailableCategories.has(originalName)) {
          return { original: originalName, display: displayName };
        }
        return null;
      })
      .filter(Boolean);
  };

  return {
    nature: createOrderedList(natureOrder),
    culture: createOrderedList(cultureOrder),
  };
});

const isSeasonFilterActive = computed(() => {
  if (store.activeCategories.size === 0) return true;
  return ![...store.activeCategories].every(cat => CULTURAL_CATEGORIES_DB.includes(cat));
});

const toggleCategory = (cat) => {
  store.toggleCategory(cat);
};
</script>

<style scoped>

/* 自定义悬浮提示框样式 */
.custom-tooltip {
  position: fixed;
  background: rgba(20, 20, 20, 0.85);
  color: #fff;
  padding: 5px 12px;
  font-size: 12px;
  font-family: var(--font-sans);
  white-space: nowrap;
  z-index: 1000;
  pointer-events: none;
  backdrop-filter: blur(2px);

  /* 动效核心：默认状态 (隐藏时) */
  opacity: 0;
  transform: translateX(-50%) translateY(5px); /* 初始位置在目标下方5px */
  transition: all 0.2s ease-out; /* 为所有属性变化添加0.2秒的过渡 */
}

.custom-tooltip.no-transition {
  transition: none !important;
}

/* 当.visible类被添加时 (显示时) */
.custom-tooltip.visible {
  opacity: 1;
  transform: translateX(-50%) translateY(0); /* 移动到最终位置 */
}

.custom-tooltip::after {
content: '';
position: absolute;
bottom: 100%; 
left: calc(50% + 8px); 
transform: translateX(-50%);
border: 5px solid transparent;
border-bottom-color: rgba(20, 20, 20, 0.85);
} 

/* --- 主容器 --- */
.ui-overlay-container {
position: fixed;
top: 0;
left: 0;
width: 100%;
height: 100%;
pointer-events: none;
z-index: 1;
color: #222;
}
.ui-overlay-container > * {
pointer-events: auto;
}

/* --- UI Wrapper & Collapse Animation --- */
.ui-wrapper {
position: relative;
width: 100%;
height: 100%;
opacity: 1;
transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
pointer-events: none;
}
.ui-wrapper > *, .toggle-ui-button {
pointer-events: auto;
}
.ui-wrapper.collapsed {
opacity: 0;
pointer-events: none;
}
.ui-wrapper > * {
transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.ui-wrapper.collapsed .scene-header,
.ui-wrapper.collapsed .project-info-panel,
.ui-wrapper.collapsed .panel-section {
transform: translateY(-170px);
}
.ui-wrapper.collapsed .category-filter {
transform: translate(-100px, -50%);
}
.ui-wrapper.collapsed .scene-footer {
transform: translateY(100px);
}

/* --- 新增: UI面板通用背景样式 --- */
.scene-header,
.project-info-panel,
.category-filter,
.season-filter,
.altitude-filter,
.scene-footer {
  /* background: rgba(255, 255, 255, 0.75); */
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  /* border: 1px solid rgba(255, 255, 255, 1); */
  /* box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05); */
  padding: 20px;
  box-sizing: border-box;
}


/* --- 顶部标题 --- */
.scene-header { position: absolute; top: 40px; left: 40px; cursor: pointer; }
.main-title {font-family: "xz"; font-size: 42px; font-weight: 500; margin: 0; letter-spacing: 2px; }
.subtitle { font-size: 12px; font-weight: 400; margin: 12px 0 0 4px; letter-spacing: 1px; color: #666; text-transform: uppercase; }

/* --- 项目信息面板 --- */
.project-info-panel {
position: absolute;
top: 145px; /* 已调整: 为标题面板的背景和内边距留出空间 */
left: 40px;
width: 280px;
font-size: 12px;
line-height: 1.6;
color: #555;
opacity: 0;
transform: translateY(-10px);
transition: opacity 0.3s ease, transform 0.3s ease;
pointer-events: none;
}
.project-info-panel.visible { opacity: 1; transform: translateY(0); }

/* --- 筛选器通用样式 --- */
.panel-section {
position: absolute;
display: flex;
align-items: center;
gap: 15px;
}
.panel-section.disabled { opacity: 0.4; pointer-events: none; }
.filter-label {
font-size: 13px;
font-weight: 500;
color: #222;
}

/* --- 分类筛选 (左侧) --- */
.category-filter {
left: 40px;
top: 50%;
transform: translateY(-50%);
width: 205px; /* 已调整: 适应新的内边距 */
flex-direction: column;
align-items: flex-start;
gap: 0;
}
.category-subtitle {
font-size: 12px;
font-weight: 500;
color: #888;
margin: 20px 0 12px 0;
text-transform: uppercase;
letter-spacing: 1px;
padding-left: 10px;
border-left: 2px solid #ccc;
}
.category-group:first-child .category-subtitle {
margin-top: 0;
}
.tag-group { display: flex; flex-wrap: wrap; gap: 8px; }
.tag {
background: transparent;
border: 1px solid rgba(0, 0, 0, 0.15);
color: #555;
padding: 4px 14px;
font-size: 12px;
cursor: pointer;
transition: all 0.25s ease;
}
.tag:hover { border-color: #000; color: #000; }
.tag.active { background: #333; color: #fff; border-color: #333; }

/* --- 季节筛选 (顶部居中) --- */
.season-filter {
top: 40px;
left: 50%;
transform: translateX(-50%);
}

/* 季节按钮组 */
.button-group { display: flex; gap: 10px; }
.button-group button {
flex-grow: 1;
background: transparent;
border: none;
border-bottom: 2px solid rgba(0, 0, 0, 0.15);
color: #777;
padding: 8px 10px;
font-size: 13px;
cursor: pointer;
transition: all 0.25s ease;
font-family: var(--font-sans);
}
.button-group button:hover { color: #000; border-bottom-color: rgba(0, 0, 0, 0.4); }
.button-group button.active { color: #000; font-weight:500; border-bottom-color: #000; }


.altitude-filter {
top: 40px;
right: 40px;
}
.range-slider {
display: flex;
flex-direction: column;
align-items: flex-start;
}
.slider-values {
font-size: 12px;
color: #333;
font-family: var(--font-sans);
width: 200px;
display: flex;
justify-content: space-between;
margin-top: 4px;
padding: 0 8px; 
}

.slider-track-container { position: relative; width: 180px; height: 20px; display: flex; align-items: center; }
.slider-track, .slider-track-highlight { position: absolute; height: 2px; border-radius: 2px; top: 50%; transform: translateY(-50%); left: 8px; right: 8px; }
.slider-track { background-color: rgba(0, 0, 0, 0.1); }
.slider-track-highlight { background-color: #333; z-index: 2; left: unset; right: unset; }
.thumb { -webkit-appearance: none; appearance: none; pointer-events: none; position: absolute; width: 100%; height: 2px; top: 50%; transform: translateY(-50%); background: transparent; outline: none; padding: 0; margin: 0; }
.thumb::-webkit-slider-thumb {
-webkit-appearance: none; appearance: none;
pointer-events: all;
width: 16px; height: 16px;
background: #333;
/* border: 3px solid #fff; */
/* box-shadow: 0 0 0 1.5px #333; */
border-radius: 50%;
cursor: grab;
position: relative;
z-index: 5;
transition: transform 0.2s ease;
}
.thumb::-webkit-slider-thumb:active { cursor: grabbing; transform: scale(1.15); }

/* --- 底部状态栏 --- */
.scene-footer {
position: absolute;
bottom: 40px;
left: 50%;
transform: translateX(-50%);
text-align: center;
}
.status-text { font-family: var(--font-sans); font-size: 11px; letter-spacing: 0.5px; color: #777; }
.hint-text { font-size: 10px; color: rgba(0, 0, 0, 0.3); margin-top: 6px; }


.control-toggle-wrapper {
position: absolute;
bottom: 40px;
right: 40px;
display: flex;
flex-direction: column;
align-items: center;
gap: 8px; 
}

.toggle-label {
font-size: 10px;
color: rgba(0, 0, 0, 0.4);
pointer-events: none; /* 让文字不影响鼠标点击 */
}
.toggle-ui-button {
width: 50px;
height: 50px;
background: transparent;
border: 1px solid rgba(0,0,0,0.1);
border-radius: 50%;
color: #333;
cursor: pointer;
display: flex;
align-items: center;
justify-content: center;
transition: all 0.3s ease;
}
.toggle-ui-button:hover {
border-color: #000;
transform: scale(1.05);
}
.toggle-ui-button .icon-sliders {
transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}
.toggle-ui-button .icon-sliders.collapsed {
transform: rotate(90deg);
}
</style>